/**
 * VitalSignsCard — Iter 74 refactor.
 * Lifted out of ObjectiveAssessment.jsx. Pure presentational — takes the
 * current vitals object + a setVital handler and a per-key isAbnormal check.
 *
 * Why extract this one first: it's the most self-contained block in
 * ObjectiveAssessment (no shared row-edit handlers, no scribe wiring, no
 * special-test catalogue). MMT and SpecialTests will follow the same pattern
 * but need shared updRow/addRow/delRow + AI-scribe plumbing.
 */
import React from 'react';
import { Activity, AlertTriangle } from 'lucide-react';

const VITAL_FIELDS = [
  ['bp',   'BP',   'mmHg'],
  ['hr',   'HR',   'bpm'],
  ['rr',   'RR',   'br/min'],
  ['spo2', 'SpO₂', '%'],
  ['temp', 'Temp', '°C'],
];

export default function VitalSignsCard({ vitals = {}, setVital, isVitalAbnormal, Section }) {
  return (
    <Section title="Vital signs" icon={Activity} testid="sec-vitals">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {VITAL_FIELDS.map(([k, label, unit]) => {
          const val = vitals?.[k] || '';
          const abnormal = val && isVitalAbnormal(k, val);
          return (
            <label key={k}
                   className={`block rounded-lg border p-3 transition ${abnormal ? 'border-[#B83A3A]/60 bg-[#FFF5F5]' : 'border-soft bg-bg-secondary/30'}`}
                   data-print-field={`fld-v-${k}`}
                   data-testid={`vitals-card-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1 flex items-center justify-between">
                <span>{label}</span>
                {abnormal && <AlertTriangle className="w-3.5 h-3.5 text-[#B83A3A]" data-testid={`vitals-warn-${k}`}/>}
              </div>
              <div className="flex items-baseline gap-1">
                <input
                  className={`input !p-1 !border-0 !bg-transparent text-2xl md:text-3xl font-bold font-mono leading-none w-full ${abnormal ? 'text-[#B83A3A]' : 'text-[#0F1B2D]'}`}
                  value={val}
                  onChange={(e) => setVital(k, e.target.value)}
                  data-testid={`v-${k}`}
                  placeholder={k === 'bp' ? '120/80' : '—'}
                />
                <span className="text-[10px] text-fg-muted">{unit}</span>
              </div>
              {abnormal && (
                <div className="text-[10px] text-[#B83A3A] font-semibold mt-1 uppercase tracking-wider"
                     data-testid={`vitals-flag-${k}`}>
                  Out of range
                </div>
              )}
            </label>
          );
        })}
      </div>
      <p className="text-[10px] text-fg-muted mt-3">
        Normal: BP 90/60–130/85 · HR 60–100 · RR 12–20 · SpO₂ ≥95% · Temp 36.1–37.5°C.
      </p>
    </Section>
  );
}
